// ============================================
// COMMUNITY HUB — UTILITY FUNCTIONS
// ============================================

import type { Post, Comment, CommunityMember, UserPlan } from './types';

// ─── DATE FORMATTERS ───────────────────────
export function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  const diffWeek = Math.floor(diffDay / 7);
  const diffMonth = Math.floor(diffDay / 30);
  const diffYear = Math.floor(diffDay / 365);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  if (diffWeek < 4) return `${diffWeek}w ago`;
  if (diffMonth < 12) return `${diffMonth}mo ago`;
  return `${diffYear}y ago`;
}

export function formatFullDate(dateString: string): string {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(dateString));
}

export function formatDateShort(dateString: string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(dateString));
}

// ─── NUMBER FORMATTERS ─────────────────────
export function formatCount(count: number): string {
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
  if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`;
  return String(count);
}

export function formatCurrency(amountInCents: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amountInCents / 100);
}

// ─── TEXT FORMATTERS ───────────────────────
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).trim() + '…';
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

export function getInitials(name: string): string {
  return name
    .split(' ')
    .map(part => part.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

// ─── VALIDATORS ────────────────────────────
export function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

export const FILE_VALIDATION = {
  image: {
    maxSize: 10 * 1024 * 1024, // 10MB
    allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'],
  },
  file: {
    maxSize: 50 * 1024 * 1024, // 50MB
    allowedTypes: [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain',
      'text/csv',
    ],
  },
};

export function validateFile(file: File, type: 'image' | 'file'): { valid: boolean; error?: string } {
  const config = FILE_VALIDATION[type];
  if (!config.allowedTypes.includes(file.type)) {
    return { valid: false, error: `File type not allowed. Allowed: ${config.allowedTypes.join(', ')}` };
  }
  if (file.size > config.maxSize) {
    const maxMB = config.maxSize / (1024 * 1024);
    return { valid: false, error: `File too large. Maximum ${maxMB}MB.` };
  }
  return { valid: true };
}

// ─── CONTENT SANITIZER ─────────────────────
export function sanitizeHTML(html: string): string {
  const div = document.createElement('div');
  div.textContent = html;
  return div.innerHTML;
}

export function sanitizeText(text: string): string {
  return text
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

// ─── PERMISSION CHECKER ────────────────────
export function canPerformAction(
  userPlan: UserPlan,
  action: string,
  context?: { isOwner?: boolean; isMember?: boolean; isAdmin?: boolean }
): boolean {
  switch (action) {
    case 'create_community':
      return userPlan !== 'starter';
    case 'post':
    case 'comment':
    case 'like':
    case 'vote':
    case 'rsvp':
      return userPlan !== 'starter' && (context?.isMember ?? false);
    case 'manage_members':
      return context?.isAdmin ?? false;
    case 'delete_community':
      return context?.isOwner ?? false;
    case 'monetize':
      return userPlan !== 'starter';
    case 'analytics':
      return userPlan === 'enterprise';
    case 'export_members':
      return userPlan === 'enterprise';
    default:
      return false;
  }
}

// ─── SORT & FILTER HELPERS ─────────────────
export function sortPosts(posts: Post[], sortBy: string, currentUserId?: string | null): Post[] {
  const sorted = [...posts];

  switch (sortBy) {
    case 'latest':
      return sorted.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    case 'top':
      return sorted.sort((a, b) => (b.likes?.length || 0) - (a.likes?.length || 0));
    case 'pinned':
      return sorted.sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0));
    case 'most_commented':
      return sorted.sort((a, b) => (b.commentCount || 0) - (a.commentCount || 0));
    case 'my_posts':
      return sorted.filter(p => {
        const authorId = typeof p.author === 'string' ? p.author : p.author?._id;
        return authorId === currentUserId;
      });
    default:
      return sorted;
  }
}

// ─── COLOR GENERATOR ───────────────────────
export function generateAvatarColor(name: string): string {
  const colors = [
    '#8b5cf6', '#6366f1', '#4f46e5', '#7c3aed',
    '#a855f7', '#d946ef', '#ec4899', '#f43f5e',
    '#ef4444', '#f97316', '#eab308', '#22c55e',
    '#14b8a6', '#06b6d4', '#3b82f6', '#2563eb',
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}

// ─── PLATFORM ICONS ────────────────────────
export function getSocialPlatformIcon(platform: string): string {
  const icons: Record<string, string> = {
    tiktok: '🎵',
    facebook: '📘',
    youtube: '▶️',
    instagram: '📷',
    x_twitter: '🐦',
    linkedin: '💼',
    discord: '💬',
    slack: '💎',
  };
  return icons[platform] || '🔗';
}

// ─── COMMISSION DISPLAY ────────────────────
export function formatCommissionBreakdown(
  totalRevenue: number,
  commissionRate: number = 0.15
): {
  platformFee: number;
  ownerEarnings: number;
  platformFeeDisplay: string;
  ownerEarningsDisplay: string;
  totalDisplay: string;
} {
  const platformFee = Math.round(totalRevenue * commissionRate);
  const ownerEarnings = totalRevenue - platformFee;

  return {
    platformFee,
    ownerEarnings,
    platformFeeDisplay: formatCurrency(platformFee),
    ownerEarningsDisplay: formatCurrency(ownerEarnings),
    totalDisplay: formatCurrency(totalRevenue),
  };
} 